<template>
  <div class="relative">
    <Button class="bg-[var(--color-orange)] text-white rounded-full px-4 h-10 my-0 mx-4"
      icon="pi pi-filter" @click="toggle"
      :label="$t('UserAdministrationComponent.options.filter')"
      :badge="filterStatus" unstyled />
    <Popover ref="op" class="w-full sm:w-96 mx-2">
      <div class="box-border overflow-y-auto max-h-[500px] p-1">
        <div class="flex flex-row w-full justify-between items-center py-0 px-4">
          <div class="flex flex-row items-center gap-3">
            <p>{{ $t("UserAdministrationComponent.options.filter") }}</p>
            <OverlayBadge severity="danger" v-if="filterCount" :value="filterCount" size="small"/>
          </div>
          <p @click.prevent="deleteFilters" class="cursor-pointer text-[var(--color-orange)]">
            {{ $t("UserAdministrationComponent.filterOptions.deleteAll") }}
          </p>
        </div>
        <div class="text-gray-500">
          <div class="flex w-full justify-between items-center py-0 px-4 cursor-pointer
            border-t border-gray-500" @click.prevent="isOpenTags = !isOpenTags">
            <div class="flex flex-row items-center gap-2">
              <p>{{ $t("UserAdministrationComponent.filterOptions.tags") }}</p>
              <OverlayBadge severity="danger" v-if="checkedTags.length !== 0" />
            </div>
            <font-awesome-icon icon="fa-chevron-up" v-if="isOpenTags"/>
            <font-awesome-icon icon="fa-chevron-down" v-else/>
          </div>
          <div class="py-1 pl-6 overflow-y-auto" v-if="isOpenTags">
            <div v-for="identifier in tagIdentifiers" :key="identifier">
              <div class="flex w-full justify-between items-center py-0 px-4 cursor-pointer border-t
                border-gray-500" @click.prevent="identifier.open = !identifier.open">
                <p>{{ identifier.name }}</p>
                <font-awesome-icon icon="fa-chevron-up" v-if="identifier.open"/>
                <font-awesome-icon icon="fa-chevron-down" v-else/>
              </div>
              <div v-if="identifier.open" class="py-1 pl-4">
                <div v-for="tag in getFilterDataTags.filter((tag) =>
                  tag.identifier.en === identifier.name || tag.identifier.de === identifier.name)"
                  :key="tag" class="flex flex-row items-center">
                    <Checkbox v-model="checkedTags" :inputId="`tag-${tag._id}`" :value="tag"/>
                    <p class="ml-2">{{ tag.tag[lang] }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="text-gray-500">
          <div class="flex w-full justify-between items-center py-0 px-4 cursor-pointer
            border-t border-gray-500" @click.prevent="isOpenTeams = !isOpenTeams">
            <div class="flex flex-row items-center gap-2">
              <p>{{ $t("UserAdministrationComponent.filterOptions.teams") }}</p>
              <OverlayBadge severity="danger" v-if="checkedTeams.length !== 0" />
            </div>
            <font-awesome-icon icon="fa-chevron-up" v-if="isOpenTeams"/>
            <font-awesome-icon icon="fa-chevron-down" v-else/>
          </div>
          <div class="py-1 pl-6 overflow-y-auto" v-if="isOpenTeams">
            <div v-for="team in getFilterDataTeams" :key="team" class="flex flex-row items-center">
              <Checkbox v-model="checkedTeams" :value="team"/>
              <p class="ml-2">{{ team.name }}</p>
            </div>
          </div>
        </div>
        <div class="text-gray-500">
          <div class="flex w-full justify-between items-center py-0 px-4 cursor-pointer border-t
          border-gray-500" @click.prevent="isOpenRegistrationPeriod = !isOpenRegistrationPeriod">
            <div class="flex flex-row items-center gap-2">
              <p>
                {{ $t("UserAdministrationComponent.filterOptions.registrationPeriod") }}
              </p>
              <OverlayBadge severity="danger" v-if="startDate !== '' || endDate !== ''" />
            </div>
            <div v-if="startDate !== '' || endDate !== ''">
              <Chip v-if="startDate !== '' && endDate !== ''"
                :label="`${formatDatePickerDate(startDate)} - ${formatDatePickerDate(endDate)}`"
                removable @remove="clearMemberSinceDates" />
              <Chip v-else-if="startDate !== ''"
                :label="`${$t('UserAdministrationComponent.filterOptions.from')}
                ${formatDatePickerDate(startDate)}`"
                removable @remove="clearMemberSinceDates" />
              <Chip v-else-if="endDate !== ''"
                :label="`${$t('UserAdministrationComponent.filterOptions.before')}
                ${formatDatePickerDate(endDate)}`"
                removable @remove="clearMemberSinceDates" />
            </div>
            <font-awesome-icon icon="fa-chevron-up" v-if="isOpenRegistrationPeriod"/>
            <font-awesome-icon icon="fa-chevron-down" v-else/>
          </div>
          <div class="py-1 pl-6 overflow-y-auto px-4" v-if="isOpenRegistrationPeriod">
            <div class="flex flex-row items-center justify-evenly min-h-16 px-4">
              <input type="date" v-model="startDate" class="border p-1"/>
              -
              <input type="date" v-model="endDate" class="border p-1"/>
            </div>
          </div>
        </div>
        <div class="text-gray-500">
          <div class="flex w-full justify-between items-center py-0 px-4 cursor-pointer border-t
           border-gray-500" @click.prevent="isOpenLevel = !isOpenLevel">
            <div class="flex flex-row items-center gap-2">
              <p>Level</p>
              <OverlayBadge severity="danger" v-if="minLevel !== '' || maxLevel !== ''" />
            </div>
            <div v-if="(minLevel !== '' && minLevel !== null)
              || (maxLevel !== '' && maxLevel !== null)" class="py-0 px-3">
              <Chip v-if="minLevel !== '' && minLevel !== null
                && maxLevel !== '' && maxLevel !== null"
                :label="`${minLevel} - ${maxLevel}`"
                removable @remove="clearLevel" />
              <Chip v-else-if="minLevel !== '' && minLevel !== null"
                :label="`> ${minLevel}`"
                removable @remove="clearLevel" />
              <Chip v-else-if="maxLevel !== '' && maxLevel !== null"
                :label="`< ${maxLevel}`"
                removable @remove="clearLevel" />
            </div>
            <font-awesome-icon icon="fa-chevron-up" v-if="isOpenLevel"/>
            <font-awesome-icon icon="fa-chevron-down" v-else/>
          </div>
          <div class="py-1 pl-6 overflow-y-auto px-8" v-if="isOpenLevel">
            <div class="flex flex-row items-center justify-around min-h-16 px-8 gap-x-2">
              <InputNumber v-model="minLevel" :useGrouping="false" fluid class="w-1/6 h-8"/>
              -
              <InputNumber v-model="maxLevel" :useGrouping="false" fluid class="w-1/6 h-8"/>
            </div>
          </div>
        </div>
        <div class="text-gray-500" v-if="getFilterDataOmindWorkshops.length">
          <div class="flex w-full justify-between items-center py-0 px-4 cursor-pointer border-t
          border-gray-500" @click.prevent="isOpenWorkshop = !isOpenWorkshop">
            <div class="flex flex-row items-center gap-2">
              <p>
                {{ $t("UserAdministrationComponent.filterOptions.workshopParticipation") }}
              </p>
              <OverlayBadge severity="danger" v-if="checkedWorkshops.length !== 0" />
            </div>
            <font-awesome-icon icon="fa-chevron-up" v-if="isOpenWorkshop"/>
            <font-awesome-icon icon="fa-chevron-down" v-else/>
          </div>
          <div class="py-1 pl-6 overflow-y-auto" v-if="isOpenWorkshop">
            <div v-for="workshop in getFilterDataOmindWorkshops" :key="workshop"
              class="flex flex-row items-center">
              <Checkbox v-if="workshop !== ''" v-model="checkedWorkshops" :value="workshop"/>
              <p class="ml-2">{{ workshop.name }} - {{ formatDate(workshop.date_workshop) }}</p>
            </div>
          </div>
        </div>
        <div class="flex items-center justify-end border-t border-gray-500">
          <button @click.prevent="updateFilters" class="bg-[var(--color-orange)] text-white py-2
            px-5 rounded-xl m-2">
            {{ $t("UserAdministrationComponent.filterOptions.filter") }}
          </button>
        </div>
      </div>
     </Popover>
  </div>
</template>

<script>
import {
  Button,
  Checkbox,
  Chip,
  InputNumber,
  OverlayBadge,
  Popover,
} from 'primevue';

export default {
  name: 'UserAdministrationFilterComponent',
  components: {
    Button,
    OverlayBadge,
    Checkbox,
    Chip,
    InputNumber,
    Popover,
  },
  emits: ['changeUserTableLoadingState'],
  data() {
    return {
      isOpenTags: false,
      isOpenTeams: false,
      isOpenTask: false,
      isOpenRegistrationPeriod: false,
      isOpenLevel: false,
      isOpenWorkshop: false,
      checkedTags: [],
      checkedTeams: [],
      checkedWorkshops: [],
      tagIdentifiers: [],
      startDate: '',
      endDate: '',
      minLevel: '',
      maxLevel: '',
      showFilter: false,
    };
  },
  computed: {
    lang() {
      return this.$i18n.locale;
    },
    getFilterTags() {
      return this.$store.getters.getFilterTags;
    },
    getFilterTeams() {
      return this.$store.getters.getFilterTeams;
    },
    getFilterWorkshops() {
      return this.$store.getters.getFilterWorkshops;
    },
    getStartDate() {
      return this.$store.getters.getStartDate;
    },
    getEndDate() {
      return this.$store.getters.getEndDate;
    },
    getMinLevel() {
      return this.$store.getters.getMinLevel;
    },
    getMaxLevel() {
      return this.$store.getters.getMaxLevel;
    },
    filterStatus() {
      let filterStatus = this.getFilterTags.length
      + this.getFilterTeams.length
      + this.getFilterWorkshops.length;
      if (this.getStartDate) {
        filterStatus += 1;
      }
      if (this.getEndDate) {
        filterStatus += 1;
      }
      if (this.getMinLevel) {
        filterStatus += 1;
      }
      if (this.getMaxLevel) {
        filterStatus += 1;
      }

      return filterStatus;
    },
    filterCount() {
      let filterCount = this.checkedTags.length
                      + this.checkedTeams.length
                      + this.checkedWorkshops.length;
      if (this.startDate) {
        filterCount += 1;
      }
      if (this.endDate) {
        filterCount += 1;
      }
      if (this.minLevel) {
        filterCount += 1;
      }
      if (this.maxLevel) {
        filterCount += 1;
      }
      return filterCount;
    },
    getFilterDataTags() {
      return this.$store.getters.filterDataTags;
    },
    getFilterDataTeams() {
      return this.$store.getters.filterDataTeams;
    },
    getFilterDataOmindWorkshops() {
      return this.$store.getters.filterDataOmindWorkshops;
    },
  },
  async mounted() {
    await this.$store.dispatch('fetchFilterData');

    await this.parseTagIdentifiers();

    this.checkedTags = this.getFilterTags.slice();
    this.checkedTeams = this.getFilterTeams.slice();
    this.checkedWorkshops = this.getFilterWorkshops.slice();
    this.startDate = this.getStartDate;
    this.endDate = this.getEndDate;
  },
  methods: {
    toggle(event) {
      this.$refs.op.toggle(event);
    },
    async updateFilters() {
      this.$emit('changeUserTableLoadingState', true);

      await this.$store.dispatch('updateFilters', {
        tagIds: this.checkedTags,
        teamIds: this.checkedTeams,
        workshopIds: this.checkedWorkshops,
        startDate: this.startDate,
        endDate: this.endDate,
        minLevel: this.minLevel,
        maxLevel: this.maxLevel,
      });
      await this.$store.dispatch('getAllFilteredUsers');
      this.$emit('changeUserTableLoadingState', false);

      this.checkedTags = this.getFilterTags.slice();
      this.checkedTeams = this.getFilterTeams.slice();
      this.checkedWorkshops = this.getFilterWorkshops.slice();
    },
    deleteFilters() {
      this.checkedTags = [];
      this.checkedTeams = [];
      this.checkedWorkshops = [];
      this.startDate = '';
      this.endDate = '';
      this.minLevel = '';
      this.maxLevel = '';

      this.updateFilters();
    },
    formatDate(originalDate) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'CET',
        hour12: false,
      };

      const getLocale = () => (
        (navigator.languages && navigator.languages.length)
          ? navigator.languages[0] : navigator.language);
      const Locale = getLocale();
      // Locale = 'en-US';
      // Locale = 'de-DE';

      const formattedDate = new Date(originalDate).toLocaleString(Locale, options);
      if (formattedDate === 'Invalid Date') {
        return ' ';
      }
      return formattedDate;
    },
    formatDatePickerDate(originalDate) {
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        timeZone: 'CET',
        hour12: false,
      };

      const getLocale = () => (
        (navigator.languages && navigator.languages.length)
          ? navigator.languages[0] : navigator.language);
      const Locale = getLocale();
      // Locale = 'en-US';
      // Locale = 'de-DE';

      const formattedDate = new Date(originalDate).toLocaleString(Locale, options);
      if (formattedDate === 'Invalid Date') {
        return ' ';
      }
      return formattedDate;
    },
    async parseTagIdentifiers() {
      const allIdentifiers = this.getFilterDataTags.map((t) => {
        if (this.lang === 'en') {
          return t.identifier.en;
        }
        if (this.lang === 'de') {
          return t.identifier.de;
        }
        return t.identifier.en;
      });

      const uniqueIdentifiers = [];

      allIdentifiers.forEach((item) => {
        if (!uniqueIdentifiers.includes(item)) {
          uniqueIdentifiers.push(item);
        }
      });

      this.tagIdentifiers = uniqueIdentifiers.map((i) => {
        const identifier = {
          name: i,
          open: false,
        };
        return identifier;
      });
    },
    clearMemberSinceDates() {
      this.startDate = '';
      this.endDate = '';
    },
    clearLevel() {
      this.minLevel = '';
      this.maxLevel = '';
    },
  },
};
</script>
